import {z} from 'zod';

import i18n from '@dms/i18n';

import {
  accountingMatrixDatagridCodeObjectField,
  AccountingMatrixDatagridConfig,
  getAccountingMatrixDatagridSharedFields,
} from '../../utils/accountingMatrixDatagridConfig';

/**
 * `paymentMethod` ("z DMS") is rendered as a plain text value for now — its real source
 * (codelist vs. DMS entity lookup) is still open, see T20-137871 point 3 / follow-up, same as
 * `billingInformation` in the shared fields. The API nests it as `{code, translation}` though,
 * so it's still wrapped like a codelist field on submit.
 *
 * There is no `itemGroup` property on this datagrid's real request body (dev swagger, checked
 * 2026-09-24) — despite the ticket text mentioning an ACCitemGroups select here, that codelist
 * is actually only used by Operation Items (T20-137887).
 *
 * `authorizationProfileCode`/`orderVariantSuffix`: the epic documents these as workshop-only
 * ("ONLY IF application=workshop THEN ... are used") and M202 (T20-137883) genuinely treats
 * them that way — optional, conditionally shown. For M205 specifically, live-tested against
 * BE: it rejects a create with a non-workshop `application` (e.g. "Salon") as mandatory-field-
 * missing when these are omitted, so — unlike M202 — they're unconditionally required here.
 *
 * `itemName` and `paymentMethod` aren't in the swagger's `required` list either, but are NOT
 * NULL DB columns (`itemCodeName`/`formaUhrady` on `ACC_Matrix205`) BE doesn't validate at the
 * API layer — live-tested, an omitted value 500s with a raw Postgres constraint error instead
 * of a clean mandatory-field message. Required here too, and given 2/2 "optional per swagger"
 * fields turned out to be NOT NULL columns, the remaining "optional" ones below are required as
 * a precaution too, rather than discovering each one the same way. Relax any of them back to
 * optional if BE confirms they're actually nullable.
 */
export const getAccountingMatrixDatagridM205ItemsConfig = (): AccountingMatrixDatagridConfig => ({
  dgCode: 'm205-items',
  fields: [
    {
      name: 'application',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldApplication'),
      type: 'choice',
      codelistCategory: 'ACCapplicationModule',
      isRequired: true,
      ...accountingMatrixDatagridCodeObjectField('application'),
    },
    {
      name: 'itemCode',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldItemCode'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'itemName',
      label: i18n.t('general.labels.name'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'warehouseNo',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldWarehouseNumber'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'authorizationProfileCode',
      label: i18n.t(
        'page.accounting.labels.accountingMatrixDatagridsFieldAuthorizationProfileCode'
      ),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'orderVariantSuffix',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldOrderVariantSuffix'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'paymentMethod',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldPaymentMethod'),
      type: 'text',
      isRequired: true,
      ...accountingMatrixDatagridCodeObjectField('paymentMethod'),
    },
    {
      name: 'revenueCentre',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldRevenueCentre'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'revenueAccount',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldRevenueAccount'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'costCentre',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldCostCentre'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'costAccount',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldCostAccount'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'warehouseAccount',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldWarehouseAccount'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'warehouseCentre',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldWarehouseCentre'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'internalDebitAccount',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldInternalDebitAccount'),
      type: 'text',
      isRequired: true,
    },
    ...getAccountingMatrixDatagridSharedFields(),
  ],
  schema: z.object({
    application: z.string().min(1),
    itemCode: z.string().min(1),
    itemName: z.string().min(1),
    warehouseNo: z.string().min(1),
    authorizationProfileCode: z.string().min(1),
    orderVariantSuffix: z.string().min(1),
    paymentMethod: z.string().min(1),
    revenueCentre: z.string().min(1),
    revenueAccount: z.string().min(1),
    costCentre: z.string().min(1),
    costAccount: z.string().min(1),
    warehouseAccount: z.string().min(1),
    warehouseCentre: z.string().min(1),
    internalDebitAccount: z.string().min(1),
    billingInformation: z.string().min(1),
    validFrom: z.string(),
    validTo: z.string(),
  }),
});
